import React, { useCallback, useRef } from 'react';

interface FilePickerBaseOptions {
  /** Фильтр системного окна выбора, как атрибут `accept`: `'image/*'`, `'.json,application/json'`. */
  accept?: string;
  /** `data-testid` скрытого input — тесты кладут в него файл через `fireEvent.change`. */
  testId?: string;
}

export type UseFilePickerOptions = FilePickerBaseOptions &
  (
    | {
        multiple?: false;
        /** Выбранный файл. Отмена окна сюда не приходит. */
        onPick: (file: File) => void;
      }
    | {
        /** Можно выбрать сразу несколько файлов — `onPick` получает их все, в порядке окна выбора. */
        multiple: true;
        onPick: (files: File[]) => void;
      }
  );

export interface FilePicker {
  /** Открыть системное окно выбора файла. Вызывать из обработчика клика — браузер требует жест пользователя. */
  open: () => void;
  /** Скрытый `<input type="file">` — отрендерить один раз где угодно рядом с кнопкой. */
  input: React.ReactElement;
}

/**
 * Выбор файла своей кнопкой: скрытый `<input type="file">` и `open()`, который его кликает. Кнопку рисует
 * вызывающий (`UDButton`, `UDIconButton`), у неё и подпись для скринридера.
 *
 * После выбора value инпута сбрасывается, поэтому тот же файл можно выбрать второй раз подряд — иначе браузер
 * не пришлёт `change`.
 */
export function useFilePicker(options: UseFilePickerOptions): FilePicker {
  const { accept, testId, multiple = false } = options;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const open = useCallback(() => inputRef.current?.click(), []);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (files.length === 0) return;
    const current = optionsRef.current;
    if (current.multiple) {
      current.onPick(files);
    } else {
      current.onPick(files[0]);
    }
  }, []);

  const input = (
    <input ref={inputRef} type="file" accept={accept} multiple={multiple} hidden data-testid={testId} onChange={handleChange} />
  );

  return { open, input };
}

export default useFilePicker;
